import { useEffect } from 'react';

import { Button } from '@flintmint/web-shared/components/button';
import { getAuthUser } from '@flintmint/web-shared/hooks/use-auth-user';

import { FullScreenOverlay, FullScreenOverlayPresence, OverlayNote, OverlayPanel } from '@/components/full-screen-overlay';
import { urlOverlays } from '@/constants/url-overlays';
import { useUrlOverlay } from '@/hooks/use-url-overlay';
import { useUserAccount } from '@/hooks/use-user-account';

import { canOpenPastInvoices } from '../billing-state';
import { useOpenPortal } from '../use-open-portal';

import styles from './past-invoices.module.scss';

/** Mounted on Subscription & Billing; a sponsored account's Invoices button opens it. */
export const PastInvoices = () => {
  const { isOpen, close } = useUrlOverlay(urlOverlays.pastInvoices, urlOverlays);

  return (
    <FullScreenOverlayPresence open={isOpen}>
      <PastInvoicesGate onClose={close} />
    </FullScreenOverlayPresence>
  );
};

/** Any account that cannot open past invoices closes the overlay unseen. */
const PastInvoicesGate = ({ onClose }: { onClose: () => void }) => {
  const { account, loading } = useUserAccount();
  const allowed = canOpenPastInvoices(account);
  const denied = !loading && !allowed;

  useEffect(() => {
    if (denied) onClose();
  }, [denied, onClose]);

  return allowed && <PastInvoicesDialog onClose={onClose} />;
};

const PastInvoicesDialog = ({ onClose }: { onClose: () => void }) => {
  const { openPortal, opening } = useOpenPortal();
  const { email } = getAuthUser();
  // Leaving would hide a portal redirect that is still on its way.
  const closeUnlessPending = () => {
    if (!opening) onClose();
  };

  return (
    <FullScreenOverlay
      title="Manage your billing"
      subtitle="View your previous payments and invoices in Stripe."
      onEscape={closeUnlessPending}
    >
      <OverlayPanel title="Your billing details">
        <div className={styles.paragraphs}>
          <div>
            <div>Your current subscription</div>
            {email && <div>{email}</div>}
          </div>
          <div>Manage your payment method and invoices here.</div>
        </div>
      </OverlayPanel>
      <OverlayNote>You’ll return to DocuMint after making your changes.</OverlayNote>
      <div className={styles.actions}>
        <Button size="big" fullWidth cornerRadius="smooth" loading={opening} onClick={() => void openPortal()}>
          Open billing portal
        </Button>
        <Button size="big" fullWidth cornerRadius="smooth" appearance="clear" disabled={opening} onClick={closeUnlessPending}>
          Back to Subscription & Billing
        </Button>
      </div>
    </FullScreenOverlay>
  );
};
